import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  authMe,
  authLogin,
  authLogout,
  authRegister,
  authTwoFactorVerify,
  setToken,
  adminImpersonate,
  adminStopImpersonation,
  orgRegister,
  orgAcceptInvitation,
  orgOpenAccount,
  orgStopOpen,
} from "@/lib/api";

const AuthContext = createContext(null);
const IMP_KEY = "besca_impersonating";
const IMP_KIND_KEY = "besca_imp_kind"; // "admin" | "org"

const readImp = () => {
  try {
    return localStorage.getItem(IMP_KEY) || null;
  } catch {
    return null;
  }
};
const writeImp = (email, kind) => {
  try {
    if (email) {
      localStorage.setItem(IMP_KEY, email);
      localStorage.setItem(IMP_KIND_KEY, kind || "admin");
    } else {
      localStorage.removeItem(IMP_KEY);
      localStorage.removeItem(IMP_KIND_KEY);
    }
  } catch {
    /* storage unavailable */
  }
};
const readKind = () => {
  try {
    return localStorage.getItem(IMP_KIND_KEY) || "admin";
  } catch {
    return "admin";
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Email of the account currently being viewed (admin or org "open account"), or null.
  const [impersonating, setImpersonating] = useState(readImp());
  // "admin" (Admin-Einblick) or "org" (Konto öffnen) — only meaningful while impersonating.
  const [impersonationKind, setImpersonationKind] = useState(readKind());

  const refresh = useCallback(async () => {
    try {
      const me = await authMe();
      setUser(me);
    } catch {
      setUser(null);
      setToken(null); // drop stale/invalid token
      writeImp(null);
      setImpersonating(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      // On the email-verification route, EmailVerify performs the auth via the
      // single-use token. Skip the boot authMe() here so its 401 (no credentials
      // yet) does not wipe the freshly-stored Bearer token (iOS dual-auth).
      if (typeof window !== "undefined" && window.location.pathname === "/email-bestaetigen") {
        setLoading(false);
        return;
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await authLogin(email, password);
    if (data?.two_factor_required) {
      return data; // { two_factor_required: true, challenge_id }
    }
    writeImp(null);
    setImpersonating(null);
    setUser(data.user);
    return data.user;
  }, []);

  // Schritt 2 der 2FA-Anmeldung: Einmalcode bestätigen -> einloggen.
  const verifyTwoFactor = useCallback(async (challengeId, code) => {
    const { user: u } = await authTwoFactorVerify(challengeId, code);
    writeImp(null);
    setImpersonating(null);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (payload) => {
    // Double opt-in: registration no longer auto-logs in; it returns
    // { status: "pending_verification", email }.
    return await authRegister(payload);
  }, []);

  // Org-Registrierung (Steuerberater/Unternehmer) – ebenfalls Double-Opt-in.
  const registerOrg = useCallback(async (payload) => {
    return await orgRegister(payload);
  }, []);

  // Einladung annehmen: legt das Konto an und loggt direkt ein.
  const acceptInvite = useCallback(async (token, password, name) => {
    const data = await orgAcceptInvitation(token, password, name);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } finally {
      writeImp(null);
      setImpersonating(null);
      setUser(null);
    }
  }, []);

  // Admin: start viewing a user's account.
  const startImpersonation = useCallback(async (userId) => {
    const { user: u } = await adminImpersonate(userId);
    writeImp(u.email, "admin");
    setImpersonating(u.email);
    setImpersonationKind("admin");
    setUser(u);
    return u;
  }, []);

  // Org manager (Steuerberater/Kanzlei-MA/Unternehmer/berechtigter Mitarbeiter):
  // open an account in their subtree ("Konto öffnen").
  const openAccount = useCallback(async (userId) => {
    const { user: u } = await orgOpenAccount(userId);
    writeImp(u.email, "org");
    setImpersonating(u.email);
    setImpersonationKind("org");
    setUser(u);
    return u;
  }, []);

  // Return one step from impersonation / "open account". With nested opens
  // (e.g. Admin → UNT → Mitarbeiter) the backend returns the remaining
  // impersonation level so we step back up instead of dropping out entirely.
  const stopImpersonation = useCallback(async () => {
    const kind = readKind();
    const res = kind === "org" ? await orgStopOpen() : await adminStopImpersonation();
    const { user: u, impersonation } = res;
    if (impersonation?.active) {
      writeImp(impersonation.email, impersonation.kind);
      setImpersonating(impersonation.email);
      setImpersonationKind(impersonation.kind);
    } else {
      writeImp(null);
      setImpersonating(null);
    }
    setUser(u);
    return u;
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      login,
      verifyTwoFactor,
      register,
      registerOrg,
      acceptInvite,
      logout,
      refresh,
      impersonating,
      impersonationKind,
      startImpersonation,
      openAccount,
      stopImpersonation,
    }),
    [
      user,
      loading,
      login,
      verifyTwoFactor,
      register,
      registerOrg,
      acceptInvite,
      logout,
      refresh,
      impersonating,
      impersonationKind,
      startImpersonation,
      openAccount,
      stopImpersonation,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
